import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { Crumb, SmeCardView } from "../../../api/types";
import { Breadcrumb } from "../../../ui/Breadcrumb";
import { Card } from "../../../ui/Card";
import { Pill } from "../../../ui/Pill";
import { StateBadge, displayState } from "../../../ui/StateBadge";
import { formatWhen } from "../shared/format";
import { KIND_OF_WAIT_WORDS, elementKindWord } from "../shared/words";
import { elementWaits, kindOfWait, lineReturned } from "../shared/waits";

/** The item whose live map shows this element: the element itself for an item, else the nearest item above it. */
export function itemIdOf(card: SmeCardView): string | null {
  if (card.element.kind === "item") return card.element.id;
  const crumbs: Crumb[] = card.breadcrumb;
  const item = [...crumbs].reverse().find((crumb) => crumb.level === "item" && crumb.element_id);
  return item?.element_id ?? null;
}

function Callout({ title, children, tone }: { title: string; children: ReactNode; tone?: "waiting" | "sent_back" | "loaded" }) {
  return (
    <div className="ra-callout" data-tone={tone}>
      <p className="ra-callout__title">{title}</p>
      <div>{children}</div>
    </div>
  );
}

/** The top of a card: where the element sits, what it is, why it waits, and the marks that matter before reading on. */
export function CardHead({ card }: { card: SmeCardView }) {
  const { element } = card;
  const lines = [...card.library_lines, ...card.lines];
  const waiting = elementWaits(element);
  const look = waiting ? "waiting" : displayState(element.state, { origin: element.origin });
  const wait = kindOfWait(element, lines);
  const mapId = itemIdOf(card);
  const returned = lines.some(lineReturned);
  const pending = element.pending;
  const removal = element.removal;

  return (
    <Card className="ra-head" aria-labelledby="ra-card-title">
      {card.breadcrumb.length > 0 && <Breadcrumb crumbs={card.breadcrumb} />}
      <h1 id="ra-card-title">{element.name}</h1>
      <div className="ra-head__marks row">
        {look && <StateBadge state={look} />}
        <Pill>{elementKindWord(element.kind)}</Pill>
        <code>{element.code}</code>
        {wait && <Pill tone="waiting">{KIND_OF_WAIT_WORDS[wait]}</Pill>}
        {element.origin === "loaded" && <Pill tone="loaded">Loaded</Pill>}
        {returned && <Pill tone="sent_back">Line returned</Pill>}
        {card.your_own_work && <Pill>Your own work</Pill>}
      </div>
      {card.state_label && <p className="ra-head__state">{card.state_label}</p>}
      <p className="ra-head__definition">{element.definition}</p>

      {card.your_own_work && (
        <Callout title="Your own work">
          You started or accepted this element yourself, because you hold both roles. It is marked so that you can see it, and it still needs your decision.
        </Callout>
      )}
      {pending && pending.status === "waiting" && pending.light && (
        <Callout title="Edited since you validated" tone="waiting">
          A small edit that changes no meaning. Look at before and after, then Validate: this is a one-click check. {pending.reason && <>Reason given: {pending.reason}</>}
        </Callout>
      )}
      {pending && pending.status === "waiting" && !pending.light && (
        <Callout title="A Next version waits for you" tone="waiting">
          {pending.changes_meaning ? "The engineer says this changes what the element means." : "The engineer says this does not change what the element means."}{" "}
          {pending.reason && <>Reason given: {pending.reason}</>}
        </Callout>
      )}
      {removal && removal.status === "waiting" && (
        <Callout title="Marked for removal" tone="waiting">
          Reason given: {removal.reason}. It is used in {removal.used_in === 1 ? "1 place" : `${removal.used_in} places`}. Validate takes it out of the files; Reject keeps it.
        </Callout>
      )}
      {element.not_used_by_us && <Callout title="Recorded as not used by us">{element.not_used_by_us.note}</Callout>}
      {card.loaded_note && (
        <Callout title="Loaded content" tone="loaded">
          {card.loaded_note}
        </Callout>
      )}
      {element.sme_comment && element.state === "sent_back" && <Callout title="Your earlier comment">{element.sme_comment}</Callout>}

      <p className="ra-head__links row">
        {mapId && <Link to={`/items/${encodeURIComponent(mapId)}/map`}>Open the item's live map</Link>}
        {mapId && <Link to={`/items/${encodeURIComponent(mapId)}`}>Open the item page</Link>}
        {element.validated_at && <span className="muted">Validated {formatWhen(element.validated_at)}{element.validated_by ? ` by ${element.validated_by.display}` : ""}.</span>}
      </p>
    </Card>
  );
}
